import { Chip, TextField } from '@mui/material'
import type { APIAgentPrompt } from '@visorpro/client/dist/types/APIAgentPrompt'
import { observer } from 'mobx-react-lite'
import type { FC } from 'react'
import { useCallback, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { BreadCrumbs } from '../../components/breadcrumbs'
import { TextButton } from '../../components/text-button'
import { useAgentPrompts } from '../../stores/agent-prompts-store'
import type { Stores } from '../../util/store'

export interface AgentPromptDetailsPageProps {
    stores: Stores
}

// Resolves what the form should start with, and only mounts the form once it
// has it: fields that render empty and fill in later can eat what you typed.
export const AgentPromptDetailsPage = observer(
    ({ stores }: AgentPromptDetailsPageProps) => {
        const { promptId } = useParams()
        const [searchParams] = useSearchParams()
        useAgentPrompts(stores)

        const isNew = promptId === undefined
        const stored = promptId
            ? stores.agentPrompts.promptsById[promptId]
            : undefined
        const fromBaseline = isNew && searchParams.get('from') === 'baseline'
        const baseline = stores.agentPrompts.baselinePrompt

        if (!isNew && !stored) {
            return stores.agentPrompts.isFetched ? (
                <Shell name="Not found">
                    <p className="color-dimmed">That prompt no longer exists.</p>
                </Shell>
            ) : (
                <Shell name="Loading…">
                    <p className="color-dimmed" data-testid="agent-prompt-loading">
                        Loading the saved prompt…
                    </p>
                </Shell>
            )
        }

        if (fromBaseline && !baseline) {
            return (
                <Shell name="New prompt">
                    <p className="color-dimmed" data-testid="agent-prompt-loading">
                        Loading the built-in baseline…
                    </p>
                </Shell>
            )
        }

        return (
            <AgentPromptForm
                // Remounts on navigation between prompts, so the next one never
                // opens holding the previous one's text.
                key={promptId ?? (fromBaseline ? 'new-from-baseline' : 'new')}
                stores={stores}
                stored={stored}
                initialName={
                    stored?.name ?? (fromBaseline ? 'Copy of built-in baseline' : '')
                }
                initialPrompt={stored?.prompt ?? (fromBaseline ? baseline : '')}
            />
        )
    },
)

// Deliberately NOT agent-prompt-details-page: that testid marks the editable
// form, so anything waiting on it waits for a form it can actually type into.
const Shell: FC<{ name: string; children: React.ReactNode }> = ({
    name,
    children,
}) => (
    <div data-testid="agent-prompt-shell" style={{ padding: '1rem' }}>
        <BreadCrumbs name={name} path={[{ name: 'Prompts', to: '/prompts' }]} />
        <div style={{ marginTop: '1rem' }}>{children}</div>
    </div>
)

interface AgentPromptFormProps {
    stores: Stores
    stored?: APIAgentPrompt
    initialName: string
    initialPrompt: string
}

const AgentPromptForm: FC<AgentPromptFormProps> = observer(
    ({ stores, stored, initialName, initialPrompt }) => {
        const navigate = useNavigate()
        // Seeded at mount from data the parent already has, so there is no
        // window where the fields are editable but not yet filled.
        const [name, setName] = useState(initialName)
        const [prompt, setPrompt] = useState(initialPrompt)

        const isNew = stored === undefined

        const onSave = useCallback(async () => {
            if (isNew) {
                const id = await stores.agentPrompts.create({ name, prompt })
                if (id) navigate(`/prompts/${id}`)
            } else {
                await stores.agentPrompts.update(stored.id, { name, prompt })
            }
        }, [isNew, name, prompt, stored, navigate, stores])

        return (
            <div
                data-testid="agent-prompt-details-page"
                style={{
                    height: '100vh',
                    display: 'flex',
                    flexDirection: 'column',
                    boxSizing: 'border-box',
                    padding: '1rem',
                    gap: '0.75rem',
                }}>
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                    }}>
                    <BreadCrumbs
                        name={isNew ? 'New prompt' : stored.name}
                        path={[{ name: 'Prompts', to: '/prompts' }]}
                    />
                    <div style={{ display: 'flex', gap: '1rem' }}>
                        <TextButton
                            onClick={onSave}
                            data-testid="agent-prompt-save"
                            disabled={stores.agentPrompts.isUpdating}
                            disabledContent="Saving...">
                            {isNew ? 'Create prompt' : 'Save prompt'}
                        </TextButton>
                        <TextButton
                            kind="danger"
                            onClick={() => navigate('/prompts')}>
                            Close
                        </TextButton>
                    </div>
                </div>

                <TextField
                    label="Name"
                    size="small"
                    value={name}
                    autoFocus
                    data-testid="agent-prompt-name"
                    onChange={e => setName(e.target.value)}
                />

                {stored && (
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                            flexWrap: 'wrap',
                        }}>
                        <span className="color-dimmed">Organizations:</span>
                        {stored.organizations.length === 0 ? (
                            <span className="color-dimmed">
                                none — these take the promoted default
                            </span>
                        ) : (
                            stored.organizations.map(organization => (
                                <Chip
                                    key={organization.id}
                                    size="small"
                                    label={organization.name}
                                />
                            ))
                        )}
                        {stored.is_default && (
                            <Chip size="small" color="success" label="DEFAULT" />
                        )}
                        <span className="color-dimmed">
                            (assign and promote from the Prompts list)
                        </span>
                    </div>
                )}

                <PlaceholderNote />

                <TextField
                    label="Prompt"
                    multiline
                    value={prompt}
                    data-testid="agent-prompt-body"
                    onChange={e => setPrompt(e.target.value)}
                    slotProps={{
                        input: {
                            style: {
                                fontFamily: 'monospace',
                                fontSize: '0.8rem',
                                alignItems: 'flex-start',
                                height: '100%',
                                overflow: 'auto',
                            },
                        },
                    }}
                    sx={{
                        flex: 1,
                        minHeight: 0,
                        '& .MuiInputBase-root': { height: '100%' },
                    }}
                />

                <p className="color-dimmed" style={{ fontSize: '0.8rem' }}>
                    ⚠ Saving replaces the live text for every organization using
                    this prompt. There is no history and no rollback.
                </p>
            </div>
        )
    },
)

// Measured, not assumed: substitution happens only in the legacy Q&A path
// (lib/prompt/questionAnswerPrompt.ts), never in the Vi 3.0 agent prompt.
const PlaceholderNote = () => (
    <p className="color-dimmed" style={{ fontSize: '0.8rem' }}>
        No placeholders are substituted here. <code>{'{{QUESTION}}'}</code>,{' '}
        <code>{'{{SECTIONS}}'}</code> and <code>{'{{LANGUAGE}}'}</code> belong to
        the legacy Test Runs prompt and are left untouched in this text — Vi 3.0
        gets the user’s question as a message and finds context with a search
        tool, so there is no sections block to substitute.
    </p>
)
